import { Alert, Box, Skeleton, Stack } from '@mui/material';

import { CallbacksTable } from '~/components/callbacks/CallbacksTable';
import { useIsPanelVisible } from '~/components/common/tabs/PanelVisibility';
import { PeriodSelect } from '~/components/serviceHistory/PeriodSelect';
import type { PeriodWindow } from '~/components/serviceHistory/periodWindow';
import { periodWindowRange } from '~/components/serviceHistory/periodWindow';
import type { CallbackScopeTarget } from '~/data/hooks/useCallbacks';
import { useCallbackSummary } from '~/data/hooks/useCallbacks';
import { TRANSIENT_SURFACE_RADIUS } from '~/theme';

export function CallbacksTab({
  scope,
  emptyMessage,
  window,
  alwaysShowProperty = false,
  onWindowChange,
}: {
  scope: CallbackScopeTarget;
  emptyMessage: string;
  window: PeriodWindow;
  alwaysShowProperty?: boolean;
  onWindowChange: (next: PeriodWindow) => void;
}) {
  // Nothing is fetched or laid out until this is the panel on screen: the summary
  // walks every elevator in scope, and the grid cannot size columns while hidden.
  const isVisible = useIsPanelVisible();
  const {
    summaries,
    window: drawnWindow,
    isLoading,
    error,
  } = useCallbackSummary(isVisible ? { ...scope, ...periodWindowRange(window) } : null);
  const showingStale = Boolean(isLoading) && summaries.length > 0;

  const isEmpty = !error && !isLoading && summaries.length === 0;

  return (
    <Box sx={{ p: 3, width: '100%' }}>
      <Stack gap={2}>
        <PeriodSelect window={window} onWindowChange={onWindowChange} />
        {error ? <Alert severity="error">Callbacks could not be loaded.</Alert> : null}
        {isEmpty ? <Alert severity="info">{emptyMessage}</Alert> : null}
        <Box sx={{ position: 'relative' }}>
          {showingStale ? (
            <Box
              role="status"
              aria-label="Loading callbacks"
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                p: 2,
                bgcolor: 'background.paper',
              }}
            >
              <Skeleton variant="text" width="25%" height={28} sx={{ bgcolor: 'grey.200' }} />
              {Array.from({ length: 6 }, (_, row) => (
                <Skeleton
                  key={row}
                  variant="rectangular"
                  height={40}
                  sx={{ bgcolor: 'grey.200', borderRadius: `${TRANSIENT_SURFACE_RADIUS}px` }}
                />
              ))}
            </Box>
          ) : null}
          {isVisible && !error && !isEmpty ? (
            <CallbacksTable
              summaries={summaries}
              loading={Boolean(isLoading) && summaries.length === 0}
              window={drawnWindow}
              alwaysShowProperty={alwaysShowProperty}
            />
          ) : null}
        </Box>
      </Stack>
    </Box>
  );
}
